/**
 * This editor's tools, and the slash commands that run them directly. Which
 * of them a session offers, and what the model reads about each, is the
 * gateway's: the `editor` profile's kits (`kits.ts`).
 */

import type { ToolDefinition } from './types';
import type { PluginSlashCommand } from '../../plugins/types';
import { freezeRegistry, registerPlugin } from '../../plugins/registry';
import { sceneTools } from './scene-tools';
import { behaviorTools } from './behavior-tools';
import { scriptTools } from './script-tools';
import { generationTools } from './generation-tools';
import { meshTools } from './mesh-tools';
import { templateTools } from './template-tools';
import { geotiffTools } from './geotiff-tools';
import { catalogTools } from './catalog-tools';
import { model3dTools } from './model3d-tools';
import { materialTools } from './material-tools';
import { guideTools } from './guide-tools';

/** The tools that need the editor's catalog sign-in, AI gateway session or CMS session. */
export const SIGNED_IN_TOOLS: readonly ToolDefinition[] = [...catalogTools, ...model3dTools, ...materialTools];

/** Every tool this editor runs, but the inference plugins', which register themselves (`plugins/loader.ts`). */
export const EDITOR_TOOLS: readonly ToolDefinition[] = [
    ...sceneTools, ...behaviorTools, ...scriptTools, ...generationTools, ...meshTools, ...templateTools, ...geotiffTools, ...guideTools,
    ...SIGNED_IN_TOOLS,
];

/** Slash commands by the tool they run. */
const SLASH_COMMANDS: Record<string, PluginSlashCommand> = {
    create_object: {
        name: 'create', description: 'Create an object from a template', usage: '<template> [name]', category: 'create',
        parseArgs: (args) => {
            const parts = args.split(/\s+/);
            return { template: parts[0] || 'cube', name: parts.slice(1).join(' ') || undefined };
        },
    },
    set_color: {
        name: 'color', description: 'Set the color of an object', usage: '<hex> [object]', category: 'scene',
        parseArgs: (args, selected) => {
            const parts = args.split(/\s+/);
            let color = parts[0] || '#FF0000FF';
            if (!color.startsWith('#')) color = '#' + color;
            return { object: parts.slice(1).join(' ') || selected || 'selected', color };
        },
    },
    select_object: {
        name: 'select', description: 'Select an object by name, or "none" to clear', usage: '<name|none>', category: 'scene',
        parseArgs: (args) => ({ object: args }),
    },
    create_script: {
        name: 'script', description: 'Create a script on the selected object', usage: '<description>', category: 'script',
        parseArgs: (args, selected) => ({ object: selected || 'selected', description: args }),
    },
    build_spatial: {
        name: 'build', description: 'Describe a scene to build procedurally', usage: '<description>', category: 'generate',
        parseArgs: (args) => ({ description: args }),
    },
    build_mesh: {
        name: 'mesh', description: 'Describe a shape to model as geometry', usage: '<description>', category: 'generate',
        parseArgs: (args) => ({ description: args }),
    },
    search_catalog: {
        name: 'search', description: 'Search the asset catalog', usage: '<query>', category: 'scene',
        parseArgs: (args) => ({ query: args }),
    },
    generate_image: {
        name: 'image', description: 'Generate an image from a text prompt', usage: '<prompt>', category: 'generate',
        parseArgs: (args) => ({ prompt: args }),
    },
    analyze_image: {
        name: 'analyze', description: 'Analyze the selected image or viewport with a vision model', usage: '<prompt>', category: 'generate',
        parseArgs: (args, selected) => ({ prompt: args, source: selected ? 'selected' : 'screenshot' }),
    },
    generate_model: {
        name: 'model', description: 'Generate a 3D model from a text prompt', usage: '<prompt>', category: 'generate',
        parseArgs: (args) => ({ prompt: args }),
    },
    generate_material: {
        name: 'material', description: 'Generate a PBR material from a text prompt', usage: '<prompt>', category: 'generate',
        parseArgs: (args) => ({ prompt: args, object: 'selected' }),
    },
};

/** Register `tools` with their slash commands, then freeze the registry. */
export function registerEditorTools(tools: readonly ToolDefinition[]): void {
    for (const tool of tools) {
        const slash = SLASH_COMMANDS[tool.tool.name];
        registerPlugin({ id: tool.tool.name, tool, ...(slash ? { slashCommands: [slash] } : {}) });
    }
    freezeRegistry();
}
